// src/pages/Login.jsx

import React, { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import axios from "../../axios";
import {
  Container,
  Paper,
  Avatar,
  Typography,
  TextField,
  Button,
  Alert,
  Link,
  Box,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LanguageIcon from "@mui/icons-material/Language";

import { useAuth } from '../../context/AuthContext'; // ✅ Import Auth Hook

export default function Login() {
  const navigate = useNavigate();
  const { setAuthData } = useAuth(); // ✅ Get setAuthData from context
  const { t, i18n } = useTranslation();
  const [password, setPassword] = useState("");
  const [mobile, setmobile]=useState("");
  const [message, setMessage] = useState(null);
  const [severity, setSeverity] = useState("error");
  const [languageAnchor, setLanguageAnchor] = useState(null);

  const handleLanguageClick = (event) => {
    setLanguageAnchor(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setLanguageAnchor(null);
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    handleLanguageClose();
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setMessage(null);

  try {
    const response = await axios.post("/User/login", {
      mobile: mobile.trim(),
      password: password.trim(),
    });

    // ✅ قراءة موحدة
    const {
      success,
      message,
      token,
      companyId,
      branchId,
      permissions = []
    } = response.data;

    // ❌ فشل منطقي من السيرفر
    if (!success) {
      throw new Error(message || "❌ فشل تسجيل الدخول");
    }

    // ✅ حفظ البيانات
    localStorage.setItem("token", token);
    localStorage.setItem("companyId", companyId);
    localStorage.setItem("branchId", branchId);
    
    if (response.data.safeId) localStorage.setItem("safeId", response.data.safeId);
    else localStorage.removeItem("safeId");

    if (response.data.stockId) localStorage.setItem("stockId", response.data.stockId);
    else localStorage.removeItem("stockId");

    // Multiple Allow Lists
    if (response.data.allowedSafeIds && response.data.allowedSafeIds.length > 0) {
        localStorage.setItem("allowedSafeIds", JSON.stringify(response.data.allowedSafeIds));
    } else {
        localStorage.removeItem("allowedSafeIds");
    }

    if (response.data.allowedStockIds && response.data.allowedStockIds.length > 0) {
        localStorage.setItem("allowedStockIds", JSON.stringify(response.data.allowedStockIds));
    } else {
        localStorage.removeItem("allowedStockIds");
    }

    if (response.data.userId) {
        localStorage.setItem("userId", response.data.userId);
    }

    setAuthData({ permissions });

    setSeverity("success");
    setMessage(message || `✅ ${t('login.messages.loginSuccess')}`);

    setTimeout(() => navigate("/dashboard"), 800);

  } catch (err) {
    // ✅ إظهار رسالة السيرفر الحقيقية
    const serverMsg =
      err.response?.data?.message ||
      err.message ||
      `❌ ${t('login.messages.loginFailed')}`;

    setSeverity("error");
    setMessage(serverMsg);
  }
};


  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center', 
        alignItems: 'center',
        bgcolor: 'grey.100', // لون خلفية هادئ
        px: 2 // حشوة أفقية صغيرة للشاشات الصغيرة
      }}
    >
      <Container maxWidth="xs" sx={{ p: 0 }}>
        {/* Language Switcher */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <IconButton
            onClick={handleLanguageClick}
            sx={{ 
              color: 'primary.main',
              bgcolor: 'white',
              boxShadow: 1,
              '&:hover': { bgcolor: 'grey.50' }
            }}
          >
            <LanguageIcon />
          </IconButton>
          <Menu
            anchorEl={languageAnchor}
            open={Boolean(languageAnchor)}
            onClose={handleLanguageClose}
          >
            <MenuItem onClick={() => changeLanguage('ar')}>
              {t('common.arabic')}
            </MenuItem>
            <MenuItem onClick={() => changeLanguage('en')}>
              {t('common.english')}
            </MenuItem>
          </Menu>
        </Box>

        <Paper
          elevation={4}
          sx={{
            p: { xs: 3, sm: 4 }, // حشوة متجاوبة: 3 للجوال و 4 للشاشات الأكبر
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderRadius: 3, // حواف دائرية أكثر
            width: '100%',
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: "primary.main", width: 56, height: 56 }}>
            <LockOutlinedIcon fontSize="large" />
          </Avatar>
          <Typography component="h1" variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
            {t('login.title')}
          </Typography>

          {message && (
            <Alert severity={severity} sx={{ width: "100%", mb: 2 }}>
              {message}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
             <TextField
              margin="normal"
              fullWidth
              label={t('login.mobile')}
              value={mobile}
              onChange={(e) => setmobile(e.target.value)}
              required
              autoFocus
              variant="outlined"
              sx={{ mb: 2 }}
            />

            <TextField
              margin="normal"
              fullWidth
              label={t('login.password')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              variant="outlined"
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large" // زر أكبر للمس
              sx={{ 
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                mb: 2,
                borderRadius: 2 
              }}
            >
              {t('login.loginButton')}
            </Button>

            <Box textAlign="center" sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('login.noAccount')}{" "}
                <Link 
                  component={RouterLink} 
                  to="/register" 
                  sx={{ 
                    fontWeight: 'bold', 
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' } 
                  }}
                >
                  {t('login.createAccount')}
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
